import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader, BrutalCard, Chip, EmptyState } from "../components/brutal";
import { Trophy, TrendingUp, Medal } from "lucide-react";

const PERIODS = [
  { key: "all", label: "All-time" },
  { key: "quarterly", label: "Quarter" },
  { key: "monthly", label: "This month" },
];

const RANK_COLORS = ["bg-brand-yellow", "bg-neutral-200", "bg-amber-300"];

export default function LeaderboardPage() {
  const [period, setPeriod] = useState("all");
  const [teamFilter, setTeamFilter] = useState("");
  const [rows, setRows] = useState([]);
  const [teams, setTeams] = useState([]);

  const load = () => {
    const params = { period };
    if (teamFilter) params.team = teamFilter;
    api.get("/leaderboard", { params }).then((r) => setRows(r.data));
  };

  useEffect(() => {
    api.get("/teams").then((r) => setTeams(r.data));
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [period, teamFilter]);

  const top3 = rows.slice(0, 3);
  const rest = rows.slice(3);

  return (
    <div>
      <PageHeader
        title="Leaderboard"
        subtitle="The best referrers right now. Volume matters, but so does conversion — so dentist-spammers need not apply."
      />

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="brutal p-1 flex gap-1" data-testid="leaderboard-period">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`px-4 py-2 rounded-xl font-semibold text-sm ${
                period === p.key ? "bg-brand-ink text-white" : "hover:bg-neutral-100"
              }`}
              data-testid={`period-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <select
          value={teamFilter}
          onChange={(e) => setTeamFilter(e.target.value)}
          className="border-2 border-black rounded-xl px-3 py-2 font-semibold bg-white"
          data-testid="filter-team"
        >
          <option value="">All teams</option>
          {teams.map((t) => (
            <option key={t.name} value={t.name}>{t.name}</option>
          ))}
        </select>
      </div>

      {rows.length === 0 ? (
        <EmptyState title="No referrers yet" description="Once employees start referring, they'll appear here." />
      ) : (
        <>
          {/* Top 3 podium */}
          <div className="grid md:grid-cols-3 gap-5 mb-6">
            {top3.map((r, i) => (
              <BrutalCard key={r.user_id} className={`p-6 ${RANK_COLORS[i]}`} data-testid={`podium-${i + 1}`}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white border-2 border-black rounded-xl flex items-center justify-center font-display font-black text-xl shadow-brutal">
                    {i === 0 ? <Trophy className="w-6 h-6" /> : <Medal className="w-6 h-6" />}
                  </div>
                  <div className="text-xs font-bold uppercase tracking-widest">Rank #{i + 1}</div>
                </div>
                <div className="mt-5 flex items-center gap-4">
                  <img
                    src={r.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(r.name)}`}
                    alt=""
                    className="w-16 h-16 rounded-2xl border-2 border-black object-cover"
                  />
                  <div className="min-w-0">
                    <div className="font-display font-black text-xl truncate">{r.name}</div>
                    <div className="text-xs text-neutral-700 truncate">{r.team || r.office || "—"}</div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-5 text-center">
                  <div className="border-2 border-black rounded-xl p-2 bg-white">
                    <div className="font-display font-black text-xl">{r.points}</div>
                    <div className="text-[10px] uppercase tracking-widest font-bold">Points</div>
                  </div>
                  <div className="border-2 border-black rounded-xl p-2 bg-white">
                    <div className="font-display font-black text-xl">{r.total_referrals}</div>
                    <div className="text-[10px] uppercase tracking-widest font-bold">Refs</div>
                  </div>
                  <div className="border-2 border-black rounded-xl p-2 bg-white">
                    <div className="font-display font-black text-xl">{r.hired_referrals}</div>
                    <div className="text-[10px] uppercase tracking-widest font-bold">Hires</div>
                  </div>
                </div>
              </BrutalCard>
            ))}
          </div>

          {/* Remaining table */}
          {rest.length > 0 && (
            <BrutalCard className="p-0 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-brand-ink text-white">
                    <tr>
                      <th className="text-left p-4 font-display font-bold w-16">#</th>
                      <th className="text-left p-4 font-display font-bold">Employee</th>
                      <th className="text-left p-4 font-display font-bold">Team</th>
                      <th className="text-right p-4 font-display font-bold">Points</th>
                      <th className="text-right p-4 font-display font-bold">Referrals</th>
                      <th className="text-right p-4 font-display font-bold">Hires</th>
                      <th className="text-right p-4 font-display font-bold">Conversion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rest.map((r) => (
                      <tr key={r.user_id} className="border-t-2 border-black/10" data-testid={`leader-row-${r.rank}`}>
                        <td className="p-4 font-display font-black">{r.rank}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <img src={r.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(r.name)}`} alt="" className="w-9 h-9 rounded-full border-2 border-black object-cover" />
                            <span className="font-semibold">{r.name}</span>
                          </div>
                        </td>
                        <td className="p-4">{r.team || r.office || "—"}</td>
                        <td className="p-4 text-right font-bold">{r.points}</td>
                        <td className="p-4 text-right">{r.total_referrals}</td>
                        <td className="p-4 text-right">{r.hired_referrals}</td>
                        <td className="p-4 text-right">
                          <Chip color={r.conversion_rate >= 50 ? "bg-brand-green" : "bg-white"}>
                            <TrendingUp className="w-3 h-3" /> {r.conversion_rate}%
                          </Chip>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </BrutalCard>
          )}
        </>
      )}
    </div>
  );
}
